import React from 'react';
import { motion } from 'framer-motion';
import { Building, Shield, Users, TrendingUp, Target } from 'lucide-react';
export function MunicipalScalePage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1131" data-mp="207y4krnpe89a">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1132" data-mp="246dnokgnywhf">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1133" data-mp="298328a33hsa9">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1134" data-mp="2nwz5ltu7ih4j">
            <span className="w-8 h-px bg-teal" data-id="element-1135" data-mp="2rxsv2rnas3zj"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1136" data-mp="2rwhqipm12s7d">
              Scale
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1137" data-mp="31vgrrn4grlkc"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1138" data-mp="31vqhd9lljqpi">
            Municipal Scale
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-1139" data-mp="9rptrnuyt97c">
            At the municipal scale, NCJI is designed to support local civic
            resilience and community strengthening. It helps local leaders,
            public institutions, and community systems better understand the
            human capability patterns shaping neighborhood outcomes, local
            trust, opportunity pathways, and civic stability.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1140" data-mp="onp3f2xq3hel">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1141" data-mp="jqyjg4iss1nw">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1142" data-mp="1vce4rxljqzcs">
            This allows cities and local governments to move beyond reactive
            problem management toward a more structured understanding of the
            human layer beneath public life.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1143" data-mp="1qjkz97i97mzn">
            Municipal systems are often closest to the real conditions people
            experience day to day. They see where youth pathways are weak, where
            misinformation spreads easily, where local support systems are
            fragmented, and where communities may lack the capability structures
            that support long-term resilience. NCJI helps make those patterns
            more visible so local institutions can respond more intentionally.
          </p>
        </motion.div>
      </section>

      {/* Local Civic Resilience */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-1144" data-mp="31yzw8bagqeur">
        <div className="max-w-4xl mx-auto" data-id="element-1145" data-mp="qczhq94c3j0t">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1146" data-mp="makxbkl0ob7f">
            <div className="flex items-center gap-4 mb-6" data-id="element-1147" data-mp="19z46e2bw4s0k">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1148" data-mp="1512m5e2qamni">
                <Shield size={24} data-id="element-1149" data-mp="1j0ljdbu04dl1" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1150" data-mp="1j0btdo8yi6fc">
                Local Civic Resilience
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1151" data-mp="3krot7yw2ho4j">
              NCJI can help local systems better understand the capabilities
              that support resilience at the community level. These include
              reasoning quality, manipulation resistance, emotional regulation,
              cooperation, self-direction, and local leadership depth. By
              understanding where these are strong or weak, municipalities can
              build better interventions and stronger support systems over time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Youth Pathways and Opportunity Access */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1152" data-mp="2ylme9s3ufy0h">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1153" data-mp="33jnxqe56lzch">
          <div className="flex items-center gap-4 mb-6" data-id="element-1154" data-mp="2fss9fqzxpm9z">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1155" data-mp="2krv36fags62n">
              <TrendingUp size={24} data-id="element-1156" data-mp="2d9f3nvw7rczy" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1157" data-mp="2d6swvyh5d1is">
              Youth Pathways and Opportunity Access
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1158" data-mp="239o9jvbkf00e">
            At the local level, one of the most important long-term questions is
            whether young people are being connected to real developmental
            pathways. NCJI can help identify where youth capability is strong
            but under-supported, where local opportunity alignment is weak, and
            where community systems need to do more to connect potential to
            education, mentorship, work, and civic participation.
          </p>
        </motion.div>
      </section>

      {/* Community Strengthening */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-1159" data-mp="1akqs1uf6h78p">
        <div className="max-w-4xl mx-auto" data-id="element-1160" data-mp="16lhxwog877cm">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1161" data-mp="16mjjvnys5e94">
            <div className="flex items-center gap-4 mb-6" data-id="element-1162" data-mp="rwydv9em742o">
              <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1163" data-mp="rzkxi7es5a19">
                <Building size={24} data-id="element-1164" data-mp="1jowdvcuytlo1" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1165" data-mp="1joeazxdss975">
                Community Strengthening
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1166" data-mp="1awcda6v4570k">
              Municipal strength depends on more than infrastructure. It also
              depends on whether communities have local trust, functioning
              support systems, healthy participation, and pathways for people to
              develop into constructive roles. NCJI helps local leaders better
              understand where these conditions are holding and where
              communities may be vulnerable to fragmentation, instability, or
              underdevelopment.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Local Leadership and Coordination */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1167" data-mp="2hkrp1f1t0jtl">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1168" data-mp="1ttcrdzkz2qoo">
          <div className="flex items-center gap-4 mb-6" data-id="element-1169" data-mp="1yqmidjo0faem">
            <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1170" data-mp="3f5e6hke4hvi4">
              <Users size={24} data-id="element-1171" data-mp="3a61eymcb4097" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1172" data-mp="18hx46qwscx3c">
              Local Leadership and Coordination
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1173" data-mp="1ckbntd8a40vo">
            Cities and municipalities rely on local leaders who can think
            clearly, cooperate well, and carry responsibility without worsening
            instability. NCJI can help support local leadership development by
            identifying where community leadership pipelines are shallow, where
            stronger readiness is needed, and where support structures can help
            cultivate more capable local responsibility over time.
          </p>
        </motion.div>
      </section>
    </div>;
}